import { of as observableOf, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { environment as env } from '../../../../environments/environment';
import { HttpService } from '../../../core/services/http.service';
import { map, catchError } from 'rxjs/operators';
import {
    TableResponseQuery,
    FilterOutputModel,
    FiltersService,
    SettingsProvider, GeneralService,
} from '../../../core';
import { HttpParams } from '@angular/common/http';
import {
    CompetitorShopListQuery,
    CompetitorShopSelectListQuery,
    CompetitorPricesQueryResultEntry,
    CompetitorStoreCreateCommand,
} from './models';
import { StoreListQuery } from '../../../domain/shops-wrapper/shops/model/models';
import { TableHelperService } from 'app/shared/tables/table-helper.service';
import {MainProductListQuery} from '../../../domain/products/model/model';

@Injectable()
export class CompetitorShopDataService {
    constructor(
        private http: HttpService,
        private tableHelperService: TableHelperService,
        private filterService: FiltersService,
        public settingsService: SettingsProvider,
        private generalService: GeneralService
    ) {}

    fetch(): Observable<CompetitorPricesQueryResultEntry[]> {
        const url = `${this.settingsService.configuration.url}${env.DataService.apiUrlGet}`;
        return this.http.get(url);
    }

    fetchData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = 'Id',
        filter: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.CompStoresDataService.apiUrlGetList}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;
        // logic to set filter values
        if (filter || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    CompetitorShopListQuery.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    MainProductListQuery.tableInfo().columns
                )
            );
            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
        );
    }

    public fetchSelectData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = 'Id',
        filter: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.CompStoresDataService.apiUrlGetList}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;
        // logic to set filter values
        if (filter || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    CompetitorShopSelectListQuery.tableInfo()
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    MainProductListQuery.tableInfo().columns
                )
            );
            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
        );
    }

    getById(id: number): Observable<StoreListQuery> {
        const url = `${this.settingsService.configuration.url}${env.CompStoresDataService.apiUrlGetDescription}/${id}`;

        return this.http.get(url).pipe(
            map((response: StoreListQuery) => {
                response = this.generalService.toUpperCamelCaseObj(response);
                response.AddressWithNR =
                    response.Street + ' ' + response.StreetNumber;
                return response;
            })
        );
    }

    getMyShops(shopId: number): Observable<[{}]> {
        // tslint:disable-next-line: max-line-length
        const url = `${this.settingsService.configuration.url}${env.CompStoresDataService.apiUrlGetOwnShopsIDs}/${shopId}/competitors`;

        return this.http.get(url).pipe(
            map((response) =>
                response.items.map((store) => {
                    store = this.generalService.toUpperCamelCaseObj(store);
                    store.AddressWithNR =
                        store.Street + ' ' + store.StreetNumber;
                    return store;
                })
            ),
            catchError((err) => {
                console.log(err);
                return observableOf([]);
            })
        );
    }

    create(form: CompetitorStoreCreateCommand): Observable<any> {
        const url = `${this.settingsService.configuration.url}${env.CompStoresDataService.apiUrlCreate}`;

        return this.http.post(url, form);
    }

    update(form: CompetitorStoreCreateCommand): Observable<any> {
        const url = `${this.settingsService.configuration.url}${env.CompStoresDataService.apiUrlUpdate}`;

        return this.http.put(url, form);
    }

    delete(id: number): Observable<any> {
        const url = `${this.settingsService.configuration.url}${env.CompStoresDataService.apiUrlDelete}/${id}`;

        return this.http.delete(url);
    }
}
